import React, { useMemo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { Components } from 'react-markdown';
import { filterMarkdownTableEmptyColumns } from '../lib/markdownTableFilter';
import './ChatResponse.css';

/**
 * SECURITY NOTE: XSS Prevention (Threat: Improper Output Handling)
 *
 * This component renders LLM-generated markdown. To prevent XSS attacks:
 *
 * 1. rehype-sanitize is applied to strip any raw HTML/script tags from output
 * 2. react-markdown v9+ strips raw HTML by default, but we add rehype-sanitize
 *    as an explicit defence-in-depth layer
 *
 * WARNING: Do NOT add rehype-raw to the rehypePlugins array without also
 * keeping rehype-sanitize. rehype-raw enables raw HTML rendering which
 * creates XSS risk if not sanitised.
 */

interface ChatResponseProps {
  text: string;
}

// Custom table wrapper for horizontal scrolling
const TableWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="table-scroll-container">
    <table>{children}</table>
  </div>
);

// Custom table cell that detects and styles "Not recorded" values
const TableCell: React.FC<React.TdHTMLAttributes<HTMLTableCellElement> & { children?: React.ReactNode }> = ({
  children,
  ...props
}) => {
  // Convert children to string for detection
  const content = React.Children.toArray(children)
    .map((child) => (typeof child === 'string' ? child : ''))
    .join('');
  const isNotRecorded = content.toLowerCase().includes('not recorded');

  return (
    <td {...props} className={isNotRecorded ? 'not-recorded' : undefined}>
      {children}
    </td>
  );
};

// Custom markdown components for table rendering
const markdownComponents: Partial<Components> = {
  table: ({ children }) => <TableWrapper>{children}</TableWrapper>,
  td: ({ children, ...props }) => <TableCell {...props}>{children}</TableCell>,
};

export function ChatResponse({ text }: ChatResponseProps) {
  // Filter out empty columns from markdown tables (silent filtering)
  const processedText = useMemo(() => filterMarkdownTableEmptyColumns(text), [text]);

  return (
    <div className="porter-response" aria-live="polite" aria-atomic={false}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={markdownComponents}
      >
        {processedText}
      </Markdown>
    </div>
  );
}
